import { AdminIntro, AdminNav, AdminCards } from "../../components/AdminWorkspace";
import { useLocation, useHistory } from "react-router-dom";
import { Code, ForumOutlined, AccountTreeOutlined, PeopleOutline, SettingsInputAntenna } from "@material-ui/icons";
import React, { useState, useEffect, useContext, useMemo } from "react";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import {
  makeStyles,
  Tabs,
  Tab,
  useMediaQuery,
  useTheme,
} from "@material-ui/core";
import {
  TuneOutlined,
  ScheduleOutlined,
  BusinessOutlined,
  PaymentOutlined,
  HelpOutlineOutlined,
  PaletteOutlined,
  HistoryOutlined,
  CheckCircleOutline,
} from "@material-ui/icons";

import SchedulesForm from "../../components/SchedulesForm";
import CompaniesManager from "../../components/CompaniesManager";
import PlansManager from "../../components/PlansManager";
import HelpsManager from "../../components/HelpsManager";
import Options from "../../components/Settings/Options";
import Whitelabel from "../../components/Settings/Whitelabel";
import FinalizacaoAtendimento from "../../components/Settings/FinalizacaoAtendimento";
import { AuditLogsTable } from "../AuditLogs";

import { i18n } from "../../translate/i18n";
import { toast } from "react-toastify";

import useCompanies from "../../hooks/useCompanies";
import { AuthContext } from "../../context/Auth/AuthContext";

import useCompanySettings from "../../hooks/useSettings/companySettings";
import useSettings from "../../hooks/useSettings";
import ForbiddenPage from "../../components/ForbiddenPage";
import { canManageTenants, canEditBrand } from "../../utils/tenant";
import { getTokens } from "../../styles/tokens";

const useStyles = makeStyles((theme) => {
  const t = getTokens(theme.palette.type, theme.palette.primary.main);
  return {
    // Abas internas (2º nível): as Tabs do tema, um pouco mais compactas que as da administração.
    // Ficam presas no topo enquanto o conteúdo da aba rola.
    tabs: {
      position: "sticky",
      top: 0,
      zIndex: 2,
      flexShrink: 0,
      minHeight: 40,
      marginBottom: 20,
      backgroundColor: t.page,
      borderBottom: `1px solid ${t.line}`,
      "& .MuiTab-root, & .MuiTab-labelIcon": { minWidth: 0, minHeight: 40, padding: "8px 12px", marginRight: 2, fontSize: 13 },
      "& .MuiTab-wrapper": { flexDirection: "row", gap: 7, whiteSpace: "nowrap" },
      "& .MuiTab-labelIcon .MuiTab-wrapper > *:first-child": { marginBottom: 0, fontSize: 17 },
    },
    container: { width: "100%", minWidth: 0, paddingBottom: 8 },
    tabHead: { marginBottom: 16 },
    tabTitle: { margin: 0, fontFamily: t.font, fontSize: 15, fontWeight: 600, lineHeight: 1.4, color: t.text },
    tabHint: { margin: "3px 0 0", maxWidth: 760, fontFamily: t.font, fontSize: 13, lineHeight: 1.55, color: t.textSecondary },
  };
});

const SettingsCustom = () => {
  const classes = useStyles();
  const location = useLocation();
  const history = useHistory();
  const theme = useTheme();
  const isCompact = useMediaQuery(theme.breakpoints.down("sm"));
  // `?tab=companies` (ex.: vindo do Financeiro da revenda) abre a aba direto
  const [tab, setTab] = useState(() => {
    try {
      const q = new URLSearchParams(window.location.search).get("tab");
      return q && ["options", "schedules", "finalizacao", "companies", "plans", "helps", "whitelabel", "auditLogs"].includes(q)
        ? q
        : "options";
    } catch (e) {
      return "options";
    }
  });
  const [schedules, setSchedules] = useState([]);
  const [company, setCompany] = useState({});
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState({});
  const [oldSettings, setOldSettings] = useState({});
  const [schedulesEnabled, setSchedulesEnabled] = useState(false);

  const { find, updateSchedules } = useCompanies();

  const { getAll: getAllSettings } = useCompanySettings();
  const { getAll: getAllSettingsOld } = useSettings();
  const { user, socket } = useContext(AuthContext);

  useEffect(() => {
    async function findData() {
      if (!user || !user.companyId) {
        return;
      }

      setLoading(true);
      try {
        const companyId = user.companyId;

        const company = await find(companyId);
        const settingList = await getAllSettings(companyId);
        const settingListOld = await getAllSettingsOld();

        setCompany(company);
        setSchedules(company.schedules);
        setSettings(settingList);
        setOldSettings(settingListOld);
        setSchedulesEnabled(settingList.scheduleType === "company");
      } catch (e) {
        toast.error(e);
      }
      setLoading(false);
    }
    findData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user && user.companyId]);

  useEffect(() => {
    if (!socket || !user || !user.companyId) return;
    const onSettingsEvent = () => {
      getAllSettingsOld().then(setOldSettings);
    };
    socket.on(`company-${user.companyId}-settings`, onSettingsEvent);
    return () => {
      socket.off(`company-${user.companyId}-settings`, onSettingsEvent);
    };
  }, [socket, user]);

  const handleTabChange = (event, newValue) => {
    setTab(newValue);
    history.replace({ pathname: location.pathname, search: `?tab=${newValue}` });
  };

  const handleSubmitSchedules = async (data) => {
    setLoading(true);
    try {
      setSchedules(data);
      await updateSchedules({ id: company.id, schedules: data });
      toast.success("Horários atualizados com sucesso.");
    } catch (e) {
      toast.error(e);
    }
    setLoading(false);
  };

  useEffect(() => {
    const value = new URLSearchParams(location.search).get("tab") || "options";
    if (["options", "schedules", "finalizacao", "companies", "plans", "helps", "whitelabel", "auditLogs"].includes(value)) setTab(value);
  }, [location.search]);

  // Revenda: permissões derivadas do `user` reativo do AuthContext (não de um snapshot),
  // para que as abas apareçam quando /auth/me ou o refresh_token atualizarem `user.company`.
  const { manageTenants, editBrand, superUser } = useMemo(
    () => ({
      manageTenants: canManageTenants(user),
      editBrand: canEditBrand(user),
      superUser: Boolean(user && user.super),
    }),
    [user]
  );
  const isSuper = () => superUser;

  return (
    <MainContainer scroll>
      {user.profile === "user" ? (
        <ForbiddenPage />
      ) : (
        <>
          <AdminIntro title="Configurações do sistema" description="Organize a operação, personalize o atendimento e conecte suas ferramentas." />
          <AdminNav />
          <Tabs
            value={tab}
            orientation="horizontal"
            indicatorColor="primary"
            textColor="primary"
            scrollButtons="auto"
            variant="scrollable"
            onChange={handleTabChange}
            className={classes.tabs}
            aria-label="Seções de configurações"
          >
            <Tab icon={<TuneOutlined />} label={i18n.t("settings.tabs.options")} value={"options"} />
            <Tab icon={<ScheduleOutlined />} label="Horários" value={"schedules"} />
            {user.profile === "admin" &&
              user.finalizacaoComValorVendaAtiva && (
                <Tab
                  icon={<CheckCircleOutline />}
                  label="Finalização do Atendimento"
                  value={"finalizacao"}
                />
              )}
            {manageTenants ? (
              <Tab
                icon={<BusinessOutlined />}
                label={i18n.t("settings.tabs.companies")}
                value={"companies"}
              />
            ) : null}
            {manageTenants ? (
              <Tab icon={<PaymentOutlined />} label={i18n.t("settings.tabs.plans")} value={"plans"} />
            ) : null}
            {isSuper() ? (
              <Tab icon={<HelpOutlineOutlined />} label={i18n.t("settings.tabs.helps")} value={"helps"} />
            ) : null}
            {editBrand ? (
              <Tab icon={<PaletteOutlined />} label="Whitelabel" value={"whitelabel"} />
            ) : null}
            {user.profile === "admin" ? (
              <Tab icon={<HistoryOutlined />} label={i18n.t("auditLogs.title")} value={"auditLogs"} />
            ) : null}
          </Tabs>

          {/* Só a aba ativa renderiza; cada aba traz os próprios cartões (sem moldura em volta) */}
          {tab === "schedules" && (
            <div className={classes.container}>
              <div className={classes.tabHead}>
                <h2 className={classes.tabTitle}>Horário de atendimento</h2>
                <p className={classes.tabHint}>
                  {schedulesEnabled ? "O controle de horário está definido por empresa. Configure os períodos abaixo." : "Para aplicar estes horários à empresa, selecione Gerenciamento por empresa em Preferências → Agendamento de expediente. O modo atual é configurado nas preferências."}
                </p>
              </div>
              <SchedulesForm
                loading={loading}
                onSubmit={handleSubmitSchedules}
                initialValues={schedules}
              />
            </div>
          )}

          {tab === "companies" && manageTenants && (
            <div className={classes.container}>
              <CompaniesManager />
            </div>
          )}

          {tab === "plans" && manageTenants && (
            <div className={classes.container}>
              <PlansManager />
            </div>
          )}

          {tab === "helps" && isSuper() && (
            <div className={classes.container}>
              <HelpsManager />
            </div>
          )}

          {tab === "whitelabel" && editBrand && (
            <div className={classes.container}>
              <Whitelabel settings={oldSettings} />
            </div>
          )}

          {tab === "auditLogs" && user.profile === "admin" && (
            <div className={classes.container}>
              <AuditLogsTable />
            </div>
          )}

          {tab === "finalizacao" && (
            <div className={classes.container}>
              <FinalizacaoAtendimento
                settings={settings}
                onSettingsChange={(newSettings) => setSettings(newSettings)}
              />
            </div>
          )}

          {tab === "options" && (
            <div className={classes.container}>
              <AdminCards items={[
                { title: "Canais de atendimento", description: "Conexões, números e configuração da API oficial.", icon: <SettingsInputAntenna />, to: "/connections" },
                { title: "Filas de atendimento", description: "Setores, menus de atendimento e encaminhamentos.", icon: <ForumOutlined />, to: "/queues" },
                { title: "Fluxos de conversa", description: "Monte jornadas com mensagens, condições e integrações.", icon: <AccountTreeOutlined />, to: "/flowbuilders" },
              ]} />
              <Options
                settings={settings}
                oldSettings={oldSettings}
                user={user}
                scheduleTypeChanged={(value) =>
                  setSchedulesEnabled(value === "company")
                }
              />
            </div>
          )}
        </>
      )}
    </MainContainer>
  );
};

export default SettingsCustom;
